import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/features/auth/useProfile';
import { useLogActivity } from '@/features/leads/useLeads';
import { fillTemplate, whatsappUrl } from './whatsapp';

interface Template {
  id: string;
  name: string;
  body: string;
  stage: string | null;
}

function useTemplates(stage: string | undefined) {
  return useQuery({
    queryKey: ['templates', 'whatsapp', stage ?? 'all'],
    queryFn: async (): Promise<Template[]> => {
      const { data, error } = await supabase
        .from('message_templates')
        .select('id, name, body, stage')
        .eq('channel', 'whatsapp')
        .order('sort_order');
      if (error) throw error;
      return (data ?? []) as unknown[] as Template[];
    },
  });
}

/**
 * Compose a WhatsApp message and log that it was sent.
 *
 * The two halves are deliberately separate actions. Opening WhatsApp hands the
 * message to another app and we never hear back, so the timeline entry is written
 * when the rep says it was sent — not optimistically on click. A timeline full of
 * messages that were never actually sent is worse than one with gaps, because the
 * leakage report (#42) reads it as activity.
 */
export function WhatsAppDialog({
  open,
  onClose,
  leadId,
  leadName,
  phone,
  stage,
}: {
  open: boolean;
  onClose: () => void;
  leadId: string;
  leadName: string;
  phone: string;
  stage?: string | undefined;
}) {
  const { t } = useTranslation();
  const { data: me } = useProfile();
  const { data: templates, isPending } = useTemplates(stage);
  const logActivity = useLogActivity();

  const [message, setMessage] = useState('');
  const [opened, setOpened] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMessage('');
    setOpened(false);
    setError(null);
  }, [open]);

  // The lead's own stage first: a rep at `offer` wants the offer follow-up, not
  // the introduction.
  const ordered = [...(templates ?? [])].sort((a, b) => {
    const aMatch = a.stage === stage ? 0 : a.stage === null ? 1 : 2;
    const bMatch = b.stage === stage ? 0 : b.stage === null ? 1 : 2;
    return aMatch - bMatch;
  });

  const apply = (template: Template) => {
    setMessage(
      fillTemplate(template.body, {
        client_name: leadName,
        agent_name: me?.full_name,
      }),
    );
  };

  const openWhatsApp = () => {
    window.open(whatsappUrl(phone, message), '_blank', 'noopener,noreferrer');
    setOpened(true);
  };

  const confirmSent = async () => {
    try {
      await logActivity.mutateAsync({
        leadId,
        type: 'whatsapp',
        body: message || null,
      });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <Modal open={open} title={t('whatsapp.title')} onClose={onClose}>
      <div className="flex flex-col gap-3">
        {error ? <Alert tone="error">{error}</Alert> : null}

        <p className="text-xs text-slate-500">{t('whatsapp.intro')}</p>

        {isPending ? (
          <Spinner />
        ) : ordered.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {ordered.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => apply(template)}
                className="rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
              >
                {template.name}
              </button>
            ))}
          </div>
        ) : null}

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-slate-700">{t('whatsapp.message')}</span>
          <textarea
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900"
          />
        </label>

        <Button onClick={openWhatsApp} disabled={!message.trim()}>
          <MessageCircle className="size-4" aria-hidden="true" />
          {t('whatsapp.openWhatsApp')}
        </Button>

        {/* Only after the rep has actually been to WhatsApp. */}
        {opened ? (
          <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs text-slate-600">{t('whatsapp.didYouSend')}</p>
            <div className="flex gap-2">
              <Button loading={logActivity.isPending} onClick={() => void confirmSent()}>
                {t('whatsapp.yesLogIt')}
              </Button>
              <Button variant="secondary" onClick={onClose}>
                {t('whatsapp.notSent')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
